// Central HTTP client for KAMS.
//
// Goals:
//  - One place to read the auth token, attach headers, parse responses, handle errors.
//  - Drop-in replacement for the hand-rolled `fetch(...)` blocks in services/ (Phase 2 migration).
//  - Returns the parsed JSON body (the backend's `{ status, value, message, ... }` envelope), so
//    existing call sites that destructure `response.status === 'success'` keep working.
//
// Migration pattern (per service function):
//   before: const r = await fetch(`${URL}/foo`, { method, headers, body }); return r.json()
//   after:  return apiPost('/foo', body)
//
// The base URL defaults to `URL` from ./URL.js but can be overridden per call (e.g. AdminURL).

import { URL as DEFAULT_BASE_URL } from './URL'
import { decryptedData } from '../views/Access'
import { logger } from '../Utils/logger'
import { readRawStorage, readStorage } from '../Utils/storage'

const STORAGE_KEYS = {
  accessToken: 'accessToken',
  refreshToken: 'refreshToken',
  legacyToken: 'USERTOKEN', // encrypted token used by the older auth flow
}

// Read the bearer token. Existing login flow stores it as JSON.stringify(token) under 'accessToken'.
const getBearerToken = () => {
  return readStorage(STORAGE_KEYS.accessToken)
}

// Read the legacy USERTOKEN (AES-encrypted with crypto-js). Used by older endpoints
// that send `Authorization: <token>` without a Bearer prefix.
const getLegacyToken = () => {
  const encrypted = readRawStorage(STORAGE_KEYS.legacyToken)
  if (!encrypted) return null
  try {
    return decryptedData(encrypted)
  } catch (_) {
    return null
  }
}

// Callback invoked on 401 / auth-error body. AppRoutes registers this on mount so the
// handler has access to both React context dispatches and React-Router navigation.
// We don't hard-import the store here to avoid circular deps.
let unauthorizedHandler = null
let lastUnauthorizedSession = null
export const setUnauthorizedHandler = (fn) => {
  unauthorizedHandler = fn
}

const getSessionKey = () => {
  return readRawStorage(STORAGE_KEYS.accessToken) || readRawStorage(STORAGE_KEYS.legacyToken)
}

const fireUnauthorized = (code) => {
  const sessionKey = getSessionKey()
  if (!unauthorizedHandler || !sessionKey || lastUnauthorizedSession === sessionKey) return
  lastUnauthorizedSession = sessionKey
  try { unauthorizedHandler(code) } catch (_) { /* swallow handler errors */ }
}

// Auth-related error keywords returned by the backend in the response body.
// These can appear on non-401 status codes (e.g. 200 with a FAILURE envelope).
const AUTH_ERROR_PATTERNS = [
  'token expired',
  'invalid token',
  'jwt expired',
  'token invalid',
  'session expired',
  'authentication failed',
  'not authenticated',
]

const isAuthErrorBody = (body) => {
  if (!body || typeof body !== 'object') return false
  const candidate = (body.message || body.error || body.status || '').toString().toLowerCase()
  return AUTH_ERROR_PATTERNS.some((p) => candidate.includes(p))
}

const hasUnauthorizedResponseCode = (body) => {
  if (!body || typeof body !== 'object') return false
  return String(body.responsecode ?? body.responseCode ?? '') === '401'
}

const inspectResponseForUnauthorized = (response) => {
  if (response.status === 401) {
    fireUnauthorized(401)
    return
  }

  // Clone the response so callers can still consume the original body.
  response.clone().json()
    .then((body) => {
      if (hasUnauthorizedResponseCode(body) || isAuthErrorBody(body)) fireUnauthorized(401)
    })
    .catch(() => {})
}

// Older services call fetch directly. Install this once so their HTTP 401 and
// `{ responsecode: 401 }` responses follow the same logout path as apiClient.
let originalFetch = null
export const installUnauthorizedInterceptor = () => {
  if (typeof window === 'undefined' || originalFetch) return
  originalFetch = window.fetch.bind(window)
  window.fetch = async (...args) => {
    const response = await originalFetch(...args)
    inspectResponseForUnauthorized(response)
    return response
  }
}

// Build the absolute URL. Accepts either a relative path ("/foo/bar") or an absolute URL.
const buildUrl = (path, baseUrl) => {
  if (/^https?:\/\//i.test(path)) return path
  const base = baseUrl || DEFAULT_BASE_URL
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`
}

const buildHeaders = (
  extra = {},
  { auth = true, authScheme = 'bearer', json = true } = {},
) => {
  const headers = { ...extra }
  if (json && !headers['Content-Type']) headers['Content-Type'] = 'application/json'
  if (auth && !headers.Authorization) {
    if (authScheme === 'legacy') {
      const token = getLegacyToken()
      if (token) headers.Authorization = token // raw, no "Bearer " prefix
    } else {
      const token = getBearerToken()
      if (token) headers.Authorization = `Bearer ${token}`
    }
  }
  return headers
}

// Core request. Returns the parsed JSON body. Network failures throw; HTTP errors
// (4xx/5xx with a JSON body) still resolve so callers can inspect `response.status`.
//
// Options:
//   baseUrl     — override the default base URL (e.g. AdminURL)
//   auth        — default true. Set false to omit the Authorization header
//                 (login, OTP, public endpoints).
//   authScheme  — 'bearer' (default) sends `Bearer <accessToken>`.
//                 'legacy' sends the decrypted USERTOKEN raw, no prefix —
//                 needed for the older endpoints in ApiService.js.
//   headers     — additional headers to merge in
//   json        — default true. Set false for FormData / multipart uploads
//                 (also lets the browser set the correct Content-Type boundary).
//   raw         — default false. Set true to return the raw Response object,
//                 useful for blob downloads / streaming.
//   onUploadProgress — optional callback for upload byte progress events.
const requestWithUploadProgress = (
  method,
  path,
  body,
  { baseUrl, auth, authScheme, extraHeaders, json, onUploadProgress, signal },
) =>
  new Promise((resolve) => {
    const xhr = new XMLHttpRequest()
    const cleanup = () => signal?.removeEventListener('abort', handleAbort)
    const handleAbort = () => xhr.abort()

    if (signal?.aborted) {
      resolve({ status: 'cancelled', value: null, message: 'Request cancelled.' })
      return
    }

    xhr.open(method, buildUrl(path, baseUrl))

    Object.entries(buildHeaders(extraHeaders, { auth, authScheme, json })).forEach(
      ([name, value]) => xhr.setRequestHeader(name, value),
    )

    xhr.upload.addEventListener('progress', onUploadProgress)
    xhr.addEventListener('error', () => {
      cleanup()
      resolve({
        status: 'error',
        value: null,
        message: 'Network request failed. Backend server may be unreachable.',
      })
    })
    xhr.addEventListener('abort', () => {
      cleanup()
      resolve({ status: 'cancelled', value: null, message: 'Request cancelled.' })
    })
    xhr.addEventListener('load', () => {
      cleanup()
      if (xhr.status === 401 || xhr.status === 403) fireUnauthorized(xhr.status)

      const text = xhr.responseText
      if (!text) {
        resolve({
          status: xhr.status >= 200 && xhr.status < 300 ? 'success' : 'error',
          value: null,
        })
        return
      }

      try {
        const parsed = JSON.parse(text)
        if (hasUnauthorizedResponseCode(parsed) || isAuthErrorBody(parsed)) fireUnauthorized(401)
        resolve(parsed)
      } catch (_) {
        resolve({ status: 'error', value: null, message: text.slice(0, 200) })
      }
    })

    signal?.addEventListener('abort', handleAbort, { once: true })
    xhr.send(body !== undefined && body !== null ? (json ? JSON.stringify(body) : body) : null)
  })

const request = async (method, path, body, opts = {}) => {
  const {
    baseUrl,
    auth = true,
    authScheme = 'bearer',
    headers: extraHeaders,
    json = true,
    raw = false,
    onUploadProgress,
    signal,
  } = opts

  if (onUploadProgress && !raw) {
    return requestWithUploadProgress(method, path, body, {
      baseUrl,
      auth,
      authScheme,
      extraHeaders,
      json,
      onUploadProgress,
      signal,
    })
  }

  const init = {
    method,
    headers: buildHeaders(extraHeaders, { auth, authScheme, json }),
    signal,
  }

  if (body !== undefined && body !== null) {
    init.body = json ? JSON.stringify(body) : body
  }

  let res
  try {
    res = await fetch(buildUrl(path, baseUrl), init)
  } catch (networkErr) {
    if (networkErr?.name === 'AbortError') {
      return { status: 'cancelled', value: null, message: 'Request cancelled.' }
    }
    logger.error('api.request.failed', { method, path, message: networkErr?.message })
    // Genuine connectivity error (DNS, offline, CORS, server down). Return structured error envelope to prevent uncaught runtime errors.
    return {
      status: 'error',
      value: null,
      message: networkErr?.message || 'Network request failed. Backend server may be unreachable.',
    }
  }

  // HTTP-level auth failure.
  if (res.status === 401 || res.status === 403) {
    fireUnauthorized(res.status)
  }

  // Binary / streaming caller wants the Response itself.
  if (raw) return res

  // Try to parse JSON regardless of status — backend returns structured errors on 4xx/5xx.
  const text = await res.text()
  if (!text) return { status: res.ok ? 'success' : 'error', value: null }
  try {
    const parsed = JSON.parse(text)
    // Body-level auth error (e.g. 200 envelope with "Token Expired" message).
    if (hasUnauthorizedResponseCode(parsed) || isAuthErrorBody(parsed)) fireUnauthorized(401)
    return parsed
  } catch (_) {
    // Non-JSON response (e.g. an HTML error page). Surface a structured error.
    return { status: 'error', value: null, message: text.slice(0, 200) }
  }
}

// apiGet accepts both (path, opts) and (path, body, opts) for parity with the
// other helpers. GET requests have no body, so the body slot — when present —
// is ignored; this lets the migration script emit a uniform 3-arg shape across
// all verbs without dropping option objects on the floor (the original 2-arg
// signature silently swallowed the `{ baseUrl, auth, ... }` arg).
export const apiGet = (path, ...rest) => {
  const opts = rest.length >= 2 ? rest[1] : rest[0]
  return request('GET', path, undefined, opts)
}
export const apiPost = (path, body, opts) => request('POST', path, body, opts)
export const apiPut = (path, body, opts) => request('PUT', path, body, opts)
export const apiPatch = (path, body, opts) => request('PATCH', path, body, opts)
export const apiDelete = (path, body, opts) => request('DELETE', path, body, opts)

// Re-export for callers that need to override per-request.
export { DEFAULT_BASE_URL }
